<template>
  <div id="layout-wrapper">
    <NavigationBar/>
    <!-- Main Content -->
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page header -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Payroll Management</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                    <li class="breadcrumb-item active">Payroll</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters and controls section -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex flex-wrap align-items-center mb-3">
                    <h5 class="card-title me-3 mb-3 mb-md-0">Payroll Period</h5>
                    <div class="ms-auto">
                      <button @click="createPayrollPeriod" class="btn btn-primary me-2">
                        <i class="mdi mdi-plus me-1"></i> Create Payroll Run
                      </button>
                      <button @click="refreshData" class="btn btn-secondary me-2">
                        <i class="mdi mdi-refresh me-1"></i> Refresh
                      </button>
                      <button @click="exportPayroll" class="btn btn-success">
                        <i class="mdi mdi-file-export me-1"></i> Export
                      </button>
                      <button @click="showEmployeeSalarySearch" class="btn btn-info">
                        <i class="mdi mdi-cash-multiple me-1"></i> Salary Management
                      </button>
                    </div>
                  </div>

                  <div class="row g-3">
                    <div class="col-md-3">
                      <label class="form-label">Period</label>
                      <select v-model="filters.periodId" class="form-select">
                        <option value="">All Periods</option>
                        <option v-for="period in payrollPeriods" :key="period.id" :value="period.id">
                          {{ period.period_name }}
                        </option>
                      </select>
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Department</label>
                      <select v-model="filters.departmentId" class="form-select">
                        <option value="">All Departments</option>
                        <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                          {{ dept.departmentName }}
                        </option>
                      </select>
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Status</label>
                      <select v-model="filters.status" class="form-select">
                        <option value="">All Statuses</option>
                        <option value="Draft">Draft</option>
                        <option value="Processing">Processing</option>
                        <option value="Processed">Processed</option>
                        <option value="Locked">Locked</option>
                        <option value="Approved">Approved</option>
                        <option value="Paid">Paid</option>
                        <option value="Closed">Closed</option>
                      </select>
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Search</label>
                      <div class="position-relative">
                        <input
                            type="text"
                            v-model="filters.search"
                            class="form-control"
                            placeholder="Search by employee name, ID..."
                        >
                        <i class="bx bx-search-alt search-icon position-absolute top-50 end-0 translate-middle-y me-2"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Statistics cards -->
          <div class="row">
            <!-- Total Payroll Amount -->
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card border-left-primary h-100 py-2">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                          <i class="mdi mdi-cash-multiple font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Total Payroll</h6>
                      <h4 class="mb-0">{{ formatCurrency(metrics.totalPayroll) }}</h4>
                      <span class="text-muted font-size-12">
                        Current period
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Employees Count -->
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card border-left-info h-100 py-2">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-info rounded-circle text-white font-size-20">
                          <i class="mdi mdi-account-group font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Employees</h6>
                      <h4 class="mb-0">{{ metrics.employeeCount || 0 }}</h4>
                      <span class="text-info font-size-12">Active employees</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pending Approvals -->
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card border-left-warning h-100 py-2">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-warning rounded-circle text-white font-size-20">
                          <i class="mdi mdi-clock-outline font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Pending Approvals</h6>
                      <h4 class="mb-0">{{ metrics.pendingApprovals || 0 }}</h4>
                      <span class="text-warning font-size-12">Awaiting review</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Next Payday -->
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card border-left-success h-100 py-2">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-success rounded-circle text-white font-size-20">
                          <i class="mdi mdi-calendar-check font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Next Payday</h6>
                      <h4 class="mb-0">{{ formatDate(metrics.nextPayday) || 'N/A' }}</h4>
                      <span class="text-success font-size-12">{{ getDaysUntilPayday() }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Payroll Periods List -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <h5 class="card-title">Payroll Periods</h5>
                </div>
                <div class="card-body">
                  <!-- Loading overlay -->
                  <div v-if="loading" class="text-center p-5">
                    <div class="spinner-border text-primary me-2" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-2">Loading payroll data...</p>
                  </div>

                  <!-- Empty state -->
                  <div v-else-if="!payrollPeriods.length" class="text-center p-5">
                    <i class="mdi mdi-cash-remove font-size-48 text-muted"></i>
                    <h5 class="mt-2">No payroll periods found</h5>
                    <p class="text-muted">Create a new payroll period to get started</p>
                    <button @click="createPayrollPeriod" class="btn btn-primary mt-2">Create Payroll Period</button>
                  </div>

                  <!-- Payroll periods table -->
                  <div v-else class="table-responsive">
                    <table class="table table-hover table-bordered align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Period Name</th>
                        <th>Date Range</th>
                        <th>Payment Date</th>
                        <th>Employees</th>
                        <th>Total Amount</th>
                        <th>Status</th>
                        <th class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="period in payrollPeriods" :key="period.id">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-xs me-2 bg-light rounded p-1 text-center">
                              <i class="mdi mdi-calendar font-size-18 text-primary"></i>
                            </div>
                            <div>
                              <h5 class="font-size-14 mb-0">{{ period.period_name }}</h5>
                              <p class="text-muted mb-0 font-size-12">{{ period.id }}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          {{ formatDate(period.start_date) }} - {{ formatDate(period.end_date) }}
                        </td>
                        <td>
                          {{ formatDate(period.payment_date) }}
                        </td>
                        <td>
                          {{ period.employee_count || 0 }}
                        </td>
                        <td>
                          {{ formatCurrency(period.total_amount) }}
                        </td>
                        <td>
                          <span class="badge" :class="getStatusBadgeClass(period.status)">
                            {{ period.status }}
                          </span>
                        </td>
                        <td class="text-center">
                          <div class="d-flex justify-content-center">
                            <button
                                @click="viewPayrollDetails(period)"
                                class="btn btn-sm btn-info me-2"
                                title="View Details"
                            >
                              <i class="mdi mdi-eye"></i>
                            </button>
                            <button
                                v-if="period.status === 'Draft'"
                                @click="processPeriod(period)"
                                class="btn btn-sm btn-primary me-2"
                                title="Process Payroll"
                            >
                              <i class="mdi mdi-play"></i>
                            </button>
                            <button
                                v-if="canApprove(period)"
                                @click="approvePeriod(period)"
                                class="btn btn-sm btn-success me-2"
                                title="Approve Payroll"
                            >
                              <i class="mdi mdi-check"></i>
                            </button>
                            <button
                                v-if="period.status === 'Approved'"
                                @click="markAsPaid(period)"
                                class="btn btn-sm btn-success"
                                title="Mark as Paid"
                            >
                              <i class="mdi mdi-cash-check"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination -->
                  <div v-if="payrollPeriods.length" class="d-flex justify-content-between align-items-center mt-4">
                    <div>
                      Showing <span class="fw-medium">{{ payrollPeriods.length }}</span> of
                      <span class="fw-medium">{{ pagination.totalPeriods }}</span> periods
                    </div>
                    <nav v-if="pagination.totalPages > 1">
                      <ul class="pagination pagination-rounded mb-0">
                        <li class="page-item" :class="{ disabled: pagination.currentPage <= 1 }">
                          <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage - 1)">
                            <i class="mdi mdi-chevron-left"></i>
                          </a>
                        </li>
                        <li
                            v-for="page in getPageNumbers()"
                            :key="page"
                            class="page-item"
                            :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                        >
                          <a v-if="page !== '...'" class="page-link" href="javascript:void(0)" @click="changePage(page)">
                            {{ page }}
                          </a>
                          <span v-else class="page-link">{{ page }}</span>
                        </li>
                        <li class="page-item" :class="{ disabled: pagination.currentPage >= pagination.totalPages }">
                          <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage + 1)">
                            <i class="mdi mdi-chevron-right"></i>
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Transactions -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
                  <h5 class="card-title">Recent Payroll Transactions</h5>
                  <a href="javascript:void(0)" @click="viewAllTransactions" class="btn btn-link">View All</a>
                </div>
                <div class="card-body">
                  <!-- Loading indicator -->
                  <div v-if="loadingTransactions" class="text-center my-3">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <span class="ms-2">Loading transactions...</span>
                  </div>

                  <!-- Empty state -->
                  <div v-else-if="!recentTransactions.length" class="text-center p-4">
                    <i class="mdi mdi-receipt font-size-48 text-muted"></i>
                    <p class="mt-2">No recent transactions found</p>
                  </div>

                  <!-- Transactions list -->
                  <div v-else class="table-responsive">
                    <table class="table table-hover align-middle">
                      <thead class="table-light">
                      <tr>
                        <th>Employee</th>
                        <th>Period</th>
                        <th>Net Salary</th>
                        <th>Payment Status</th>
                        <th>Date</th>
                        <th class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="transaction in recentTransactions" :key="transaction.id">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-xs me-2 bg-light rounded-circle p-1 text-center">
                              <i class="mdi mdi-account font-size-18 text-primary"></i>
                            </div>
                            <div>
                              <h5 class="font-size-14 mb-0">{{ transaction.employee_name }}</h5>
                              <p class="text-muted mb-0 font-size-12">{{ transaction.employee_id }}</p>
                            </div>
                          </div>
                        </td>
                        <td>{{ transaction.period_name }}</td>
                        <td>{{ formatCurrency(transaction.net_salary) }}</td>
                        <td>
                          <span class="badge" :class="getPaymentStatusBadgeClass(transaction.payment_status)">
                            {{ transaction.payment_status }}
                          </span>
                        </td>
                        <td>{{ formatDate(transaction.created_at) }}</td>
                        <td class="text-center">
                          <button
                              @click="viewTransactionDetails(transaction)"
                              class="btn btn-sm btn-info me-2"
                              title="View Details"
                          >
                            <i class="mdi mdi-eye"></i>
                          </button>
                          <button
                              @click="generatePayslip(transaction)"
                              class="btn btn-sm btn-secondary"
                              title="Generate Payslip"
                          >
                            <i class="mdi mdi-file-document"></i>
                          </button>

                          <button
                              @click="viewEmployeeSalary(transaction.employee_id)"
                              class="btn btn-sm btn-primary"
                              title="Manage Salary"
                          >
                            <i class="mdi mdi-cash-multiple"></i>
                          </button>

                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <CreatePayrollPeriod
        ref="createPayrollModal"
        :departments="departments"
        @period-created="onPeriodCreated"
    />

    <PayrollDetails
        ref="payrollDetailsModal"
        :period-id="selectedPeriodId"
        @view-transaction="onViewTransaction"
        @view-payslip="onViewPayslip"
        @view-employee-salary="viewEmployeeSalary"
    />

    <TransactionDetails
        ref="transactionDetailsModal"
        :transaction-id="selectedTransactionId"
        @generate-payslip="onGeneratePayslip"
        @view-employee-salary="viewEmployeeSalary"
    />

    <EmployeeSalary
        ref="employeeSalaryModal"
        :employee-id="selectedEmployeeId"
        @salary-updated="refreshData"
    />

    <Payslip
        ref="payslipModal"
        :transaction-id="selectedTransactionId"
    />

    <!-- Employee Search Modal -->
    <div class="modal fade" id="employeeSearchModal" tabindex="-1" aria-labelledby="employeeSearchModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="employeeSearchModalLabel">
              <i class="mdi mdi-account-search me-2"></i>Search Employee
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="employeeSearchInput" class="form-label">Search Employees</label>
              <div class="input-group">
                <input
                    type="text"
                    class="form-control"
                    id="employeeSearchInput"
                    v-model="employeeSearchKeyword"
                    placeholder="Search by name, ID, or department"
                    @input="searchEmployeesForSalary"
                    autocomplete="off"
                >
                <button class="btn btn-outline-primary" type="button" @click="searchEmployeesForSalary">
                  <i class="mdi mdi-magnify"></i>
                </button>
              </div>
            </div>

            <div class="employee-search-results mt-3">
              <div v-if="loadingEmployees" class="text-center p-3">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <span class="ms-2">Searching employees...</span>
              </div>

              <div v-else-if="filteredEmployees.length === 0" class="text-center p-3 text-muted">
                <i class="mdi mdi-account-search font-size-24"></i>
                <p>No employees found. Try a different search term.</p>
              </div>

              <div v-else class="list-group">
                <button
                    v-for="employee in filteredEmployees"
                    :key="employee.id"
                    type="button"
                    class="list-group-item list-group-item-action"
                    @click="selectEmployeeForSalary(employee.id)"
                >
                  <div class="d-flex align-items-center">
                    <div class="avatar-xs me-3 bg-light rounded-circle text-center">
                      <i class="mdi mdi-account font-size-18 text-primary"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-0">{{ employee.firstName }} {{ employee.lastName || '' }}</h6>
                      <small class="text-muted">
                        ID: {{ employee.id }} | {{ employee.departmentName || 'No Department' }}
                      </small>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Cancel</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import * as bootstrap from 'bootstrap';

import CreatePayrollPeriod from "@/components/payroll/CreatePayrollPeriod.vue";
import PayrollDetails from "@/components/payroll/PayrollDetails.vue";
import EmployeeSalary from "@/components/payroll/EmployeeSalary.vue";
import Payslip from "@/components/payroll/Payslip.vue";
import TransactionDetails from "@/components/payroll/TransactionDetails.vue";
import NavigationBar from "@/components/Navigation.vue";

export default {
  name: 'PayrollPage',
  components: {
    NavigationBar,
    CreatePayrollPeriod,
    PayrollDetails,
    EmployeeSalary,
    Payslip,
    TransactionDetails
  },
  data() {
    return {
      loading: false,
      loadingTransactions: false,
      payrollPeriods: [],
      recentTransactions: [],
      departments: [],
      filters: {
        search: '',
        periodId: '',
        departmentId: '',
        status: ''
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalPeriods: 0,
        limit: 10
      },
      metrics: {
        totalPayroll: 0,
        employeeCount: 0,
        pendingApprovals: 0,
        nextPayday: null
      },
      selectedPeriodId: null,
      selectedEmployeeId: null,
      selectedTransactionId: null,

      employeeSearchKeyword: '',
      employeeSearchTimeout: null,
      filteredEmployees: [],
      loadingEmployees: false,
    };
  },
  mounted() {
    this.getPayrollPeriods();
    this.getRecentTransactions();
    this.getDepartments();
    this.getPayrollMetrics();
  },
  methods: {
    closeModalFromEvent() {
      try {
        const el = document.getElementById('employeeSearchModal');
        if (!el) return;
        if (typeof bootstrap !== 'undefined') {
          const modal = bootstrap.Modal.getInstance(el);
          if (modal) modal.hide();
        } else {
          el.classList.remove('show');
          document.body.classList.remove('modal-open');
          const backdrop = document.querySelector('.modal-backdrop');
          if (backdrop) backdrop.remove();
        }
      } catch (e) {
        console.warn('Close employee search modal:', e);
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },
    getDaysUntilPayday() {
      if (!this.metrics.nextPayday) return 'No scheduled payday';

      const today = new Date();
      const payday = new Date(this.metrics.nextPayday);
      const diffTime = payday - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Tomorrow';
      return `${diffDays} days from now`;
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'Draft':
          return 'bg-secondary';
        case 'Processing':
          return 'bg-info';
        case 'Approved':
          return 'bg-primary';
        case 'Paid':
          return 'bg-success';
        case 'Closed':
          return 'bg-dark';
        default:
          return 'bg-secondary';
      }
    },
    getPaymentStatusBadgeClass(status) {
      switch (status) {
        case 'Pending':
          return 'bg-warning';
        case 'Processing':
          return 'bg-info';
        case 'Paid':
          return 'bg-success';
        case 'Failed':
          return 'bg-danger';
        case 'Cancelled':
          return 'bg-dark';
        default:
          return 'bg-secondary';
      }
    },
    getPageNumbers() {
      const totalPages = this.pagination.totalPages;
      const currentPage = this.pagination.currentPage;

      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      if (currentPage <= 3) {
        return [1, 2, 3, 4, '...', totalPages - 1, totalPages];
      }

      if (currentPage >= totalPages - 2) {
        return [1, 2, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      }

      return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;

      this.pagination.currentPage = page;
      this.getPayrollPeriods();
    },
    refreshData() {
      this.getPayrollPeriods();
      this.getRecentTransactions();
      this.getPayrollMetrics();
    },
    getPayrollPeriods() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getPayrollPeriods");
      data.append("page", this.pagination.currentPage);
      data.append("limit", this.pagination.limit);

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      if (this.filters.status) {
        data.append("status", this.filters.status);
      }

      if (this.filters.periodId) {
        data.append("periodId", this.filters.periodId);
      }

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data && res.data.success) {
              this.payrollPeriods = res.data.data.payrollPeriods;
              this.pagination = res.data.data.pagination;
              this.metrics = res.data.data.metrics;
            } else {
              console.error("Error fetching payroll periods:", res.data?.message || "Unknown error");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
          });
    },
    getRecentTransactions() {
      this.loadingTransactions = true;

      const data = new FormData();
      data.append("function", "getRecentTransactions");
      data.append("limit", 5);

      execute(data, authUrl)
          .then((res) => {
            this.loadingTransactions = false;

            if (res.data && res.data.success) {
              this.recentTransactions = res.data.data.transactions;
            } else {
              console.error("Error fetching recent transactions:", res.data?.message || "Unknown error");
            }
          })
          .catch((error) => {
            this.loadingTransactions = false;
            console.error("API Error:", error);
          });
    },
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data && res.data.success) {
              this.departments = res.data.data.departments;
            } else {
              console.error("Error fetching departments:", res.data?.message || "Unknown error");
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },
    getPayrollMetrics() {
      const data = new FormData();
      data.append("function", "getPayrollMetrics");

      execute(data, authUrl)
          .then((res) => {
            if (res.data && res.data.success) {
              this.metrics = res.data.data;
            } else {
              console.error("Error fetching payroll metrics:", res.data?.message || "Unknown error");
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },
    createPayrollPeriod() {
      this.$nextTick(() => {
        if (this.$refs.createPayrollModal) {
          this.$refs.createPayrollModal.showModal();
        } else {
          console.error("Create payroll modal reference not found");
        }
      });
    },
    onPeriodCreated(periodData) {
      this.getPayrollPeriods();
      console.log(`Period ${periodData.periodName} (ID: ${periodData.periodId}) created`);
    },
    viewPayrollDetails(period) {
      this.selectedPeriodId = period.id;

      this.$nextTick(() => {
        if (this.$refs.payrollDetailsModal) {
          this.$refs.payrollDetailsModal.showModal(period.id);
        } else {
          console.error("Payroll details modal reference not found");
        }
      });
    },
    onViewTransaction(transactionId) {
      this.viewTransactionDetails({ id: transactionId });
    },
    viewTransactionDetails(transaction) {
      this.selectedTransactionId = transaction.id;

      this.$nextTick(() => {
        if (this.$refs.transactionDetailsModal) {
          this.$refs.transactionDetailsModal.showModal(transaction.id);
        } else {
          console.error("Transaction details modal reference not found");
        }
      });
    },
    processPeriod(period) {
      if (!confirm(`Are you sure you want to process payroll for period "${period.period_name}"?`)) {
        return;
      }

      const data = new FormData();
      data.append("function", "processPeriod");
      data.append("periodId", period.id);

      execute(data, authUrl)
          .then((res) => {
            if (res.data && res.data.success) {
              const respData = res.data.data || {};
              const msg = res.data.message || "Payroll processed successfully.";
              if (respData.errors && respData.errors.length > 0) {
                const errList = respData.errors.slice(0, 5).join("; ");
                const more = respData.errors.length > 5 ? ` (+${respData.errors.length - 5} more)` : "";
                (this.$toast?.warning || ((m) => alert(m)))(`${msg} Skipped: ${errList}${more}`);
              } else {
                (this.$toast?.success || ((m) => alert(m)))(msg);
              }
              this.refreshData();
            } else {
              const errorMsg = res.data?.message || "An error occurred";
              if (this.$toast) {
                this.$toast.error(`Error: ${errorMsg}`);
              } else {
                alert(`Error: ${errorMsg}`);
              }
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
            if (this.$toast) {
              this.$toast.error("An error occurred while processing the payroll period.");
            } else {
              alert("An error occurred while processing the payroll period.");
            }
          });
    },
    canApprove(period) {
      return period.status === 'Processing' || period.status === 'Processed' || period.status === 'Draft';
    },
    approvePeriod(period) {
      const approvalLevel = 1;

      const data = new FormData();
      data.append("function", "approvePeriod");
      data.append("periodId", period.id);
      data.append("approvalLevel", approvalLevel);
      data.append("comments", "Approved via Payroll Management Interface");

      execute(data, authUrl)
          .then((res) => {
            if (res.data && res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              } else {
                alert(res.data.message);
              }
              this.refreshData();
            } else {
              const errorMsg = res.data?.message || "An error occurred";
              if (this.$toast) {
                this.$toast.error(`Error: ${errorMsg}`);
              } else {
                alert(`Error: ${errorMsg}`);
              }
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
            if (this.$toast) {
              this.$toast.error("An error occurred while approving the payroll period.");
            } else {
              alert("An error occurred while approving the payroll period.");
            }
          });
    },
    markAsPaid(period) {
      if (!confirm(`Are you sure you want to mark payroll period "${period.period_name}" as paid?`)) {
        return;
      }

      const paymentDate = new Date().toISOString().split('T')[0];
      const paymentReference = `PMT-${Date.now().toString().substr(-6)}`;

      const data = new FormData();
      data.append("function", "markAsPaid");
      data.append("periodId", period.id);
      data.append("paymentDate", paymentDate);
      data.append("paymentReference", paymentReference);

      execute(data, authUrl)
          .then((res) => {
            if (res.data && res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              } else {
                alert(res.data.message);
              }
              this.refreshData();
            } else {
              const errorMsg = res.data?.message || "An error occurred";
              if (this.$toast) {
                this.$toast.error(`Error: ${errorMsg}`);
              } else {
                alert(`Error: ${errorMsg}`);
              }
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
            if (this.$toast) {
              this.$toast.error("An error occurred while marking the payroll period as paid.");
            } else {
              alert("An error occurred while marking the payroll period as paid.");
            }
          });
    },
    viewAllTransactions() {
      this.$router.push({ name: 'payroll-transactions' });
    },
    generatePayslip(transaction) {
      this.selectedTransactionId = transaction.id;

      this.$nextTick(() => {
        if (this.$refs.payslipModal) {
          this.$refs.payslipModal.showModal(transaction.id);
        } else {
          console.error("Payslip modal reference not found");
        }
      });
    },
    onGeneratePayslip(transactionId) {
      this.generatePayslip({ id: transactionId });
    },
    onViewPayslip(transactionId) {
      this.generatePayslip({ id: transactionId });
    },
    showEmployeeSalarySearch() {
      this.employeeSearchKeyword = '';
      this.filteredEmployees = [];

      const modal = new bootstrap.Modal(document.getElementById('employeeSearchModal'));
      modal.show();
    },
    searchEmployeesForSalary() {
      if (this.employeeSearchTimeout) {
        clearTimeout(this.employeeSearchTimeout);
      }

      this.employeeSearchTimeout = setTimeout(() => {
        this.loadingEmployees = true;

        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("keyword", this.employeeSearchKeyword);

        execute(data, authUrl)
            .then((res) => {
              this.loadingEmployees = false;
              if (res.data && res.data.success) {
                this.filteredEmployees = res.data.data.employees;
              } else {
                this.filteredEmployees = [];
                console.error("Failed to load employees:", res.data?.message || "Unknown error");
              }
            })
            .catch((e) => {
              this.loadingEmployees = false;
              this.filteredEmployees = [];
              console.error("Error loading employees:", e);
            });
      }, 300);
    },
    selectEmployeeForSalary(employeeId) {
      const modal = bootstrap.Modal.getInstance(document.getElementById('employeeSearchModal'));
      if (modal) {
        modal.hide();
      }

      this.viewEmployeeSalary(employeeId);
    },
    exportPayroll() {
      const format = 'excel';
      let periodId = this.filters.periodId;

      if (!periodId) {
        const response = confirm("No period selected. Do you want to export all periods?");
        if (!response) {
          periodId = prompt("Enter payroll period ID to export:");
          if (!periodId) return;
        }
      }

      const data = new FormData();
      data.append("function", "exportPayrollData");
      data.append("format", format);

      if (periodId) {
        data.append("periodId", periodId);
      }

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data && res.data.success) {
              const downloadUrl = res.data.data.downloadUrl;
              const fileName = res.data.data.filename;

              const link = document.createElement('a');
              link.href = downloadUrl;
              link.setAttribute('download', fileName);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              if (this.$toast) {
                this.$toast.success(`Payroll data exported successfully as ${fileName}`);
              } else {
                alert(`Payroll data exported successfully as ${fileName}`);
              }
            } else {
              const errorMsg = res.data?.message || "An error occurred";
              if (this.$toast) {
                this.$toast.error(`Error: ${errorMsg}`);
              } else {
                alert(`Error: ${errorMsg}`);
              }
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
            if (this.$toast) {
              this.$toast.error("An error occurred while exporting the payroll data.");
            } else {
              alert("An error occurred while exporting the payroll data.");
            }
          });
    },
    viewEmployeeSalary(employeeId) {
      this.selectedEmployeeId = employeeId;

      this.$nextTick(() => {
        if (this.$refs.employeeSalaryModal) {
          this.$refs.employeeSalaryModal.showModal();
        } else {
          console.error("Employee salary modal reference not found");
        }
      });
    },
  }
};
</script>

<style scoped>
.employee-search-results {
  max-height: 300px;
  overflow-y: auto;
}

.employee-search-results .list-group-item {
  transition: background-color 0.2s;
}

.employee-search-results .list-group-item:hover {
  background-color: #f8f9fa;
}

.avatar-xs {
  height: 2rem;
  width: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.font-size-18 {
  font-size: 18px !important;
}

.font-size-24 {
  font-size: 24px !important;
}

.content-minimize {
  margin-left: 250px;
  padding: 15px;
  transition: all 0.3s;
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  font-weight: 500;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.border-left-primary {
  border-left: 4px solid #4e73df !important;
}

.border-left-success {
  border-left: 4px solid #1cc88a !important;
}

.border-left-info {
  border-left: 4px solid #36b9cc !important;
}

.border-left-warning {
  border-left: 4px solid #f6c23e !important;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.card-header {
  margin-bottom: 0;
}

@media (max-width: 992px) {
  .content-minimize {
    margin-left: 0;
  }
}
</style>